import {Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup,} from '@mui/material';
import type * as React from 'react';
import {useState} from 'react';
import type {Rental} from '@/types/Rental';
import {MaterialReactTable} from 'material-react-table';
import {type RentalUser} from "@/types/RentalUser";
import {rentalReportFileName, rentalsForToggle} from '@/utils/rentalReport';

type Props = {
    rentals : Rental[];
    user : RentalUser;
    personId ?: string;
};

const columns = [
    {
        accessorKey: 'itemNum',
        header: 'Item Number',
        sort: true,
    },
    {
        accessorKey: 'itemName',
        header: 'Description',
        sort: true,
    },
    {
        accessorKey: 'type',
        header: 'Type',
        sort: true,
    },
    {
        accessorKey: 'serialNum',
        header: 'Serial #',
        sort: true,
    },
    {
        accessorKey: 'checkoutTimestamp',
        header: 'Checkout Date',
        sort: true,
    },
    {
        accessorKey: 'inOutStatus',
        header: 'Status',
        sort: true,
    },
];

const RentalsList = ({rentals, user, personId} : Props) : React.ReactElement => {
    const [showCheckedOut, setShowCheckedOut] = useState<boolean>(true);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
    const [pdfError, setPdfError] = useState<string>('');
    const reportRowCount = rentalsForToggle(rentals, showCheckedOut).length;

    const downloadPdf = async (reportPersonId : string) : Promise<void> => {
        setIsGeneratingPdf(true);
        setPdfError('');

        try {
            const [{pdf}, {default: RentalReport}] = await Promise.all([
                import('@react-pdf/renderer'),
                import('@/components/RentalReport'),
            ]);

            const generatedAt = new Date();
            const blob = await pdf(<RentalReport
                rentals={rentals}
                personName={user.name}
                personId={reportPersonId}
                showCheckedOut={showCheckedOut}
                generatedAt={generatedAt}
            />).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = rentalReportFileName(reportPersonId, showCheckedOut, generatedAt);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            setPdfError('Unable to generate the PDF. Please try again.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return <MaterialReactTable
        columns={columns}
        data={rentals}
        initialState={{
            columnFilters: [{
                id: 'inOutStatus',
                value: 'Checked Out'
            }]
        }}

        enableColumnActions={false}
        enableColumnFilters={false}
        enableDensityToggle={false}
        enableFilterMatchHighlighting={false}
        enableFullScreenToggle={false}
        enableGlobalFilter={false}
        enableHiding={false}

        renderTopToolbarCustomActions={({table}) => {
            return <Stack
                sx={{
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <Stack
                    sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Box sx={{typography: 'h5', px: 2, pt: 2, fontWeight: 700}}>Item Rental System</Box>
                </Stack>
                <Stack
                    sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Box sx={{typography: 'h6', p: 2}}>Displaying Records for <b>{user.name}</b></Box>
                </Stack>
                <Stack
                    sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <ToggleButtonGroup
                        color="primary"
                        value={showCheckedOut ? 'Checked Out' : 'Returned'}
                        exclusive
                        onChange={(_event, value) => {
                            if (value === null || value === 'Returned') {
                                table.setColumnFilters(prev => [...prev, {
                                    id: 'inOutStatus',
                                    value: 'Returned',
                                }])
                                setShowCheckedOut(false);
                                return;
                            }

                            if (value === 'Checked Out') {
                                table.setColumnFilters(prev => [...prev, {
                                    id: 'inOutStatus',
                                    value: 'Checked Out',
                                }])
                                setShowCheckedOut(true);
                            }
                        }}
                        aria-label="Platform"
                    >
                        <ToggleButton value={'Checked Out'}>Items Currently Checked Out</ToggleButton>
                        <ToggleButton value={'Returned'}>History</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                {personId !== undefined && personId !== '' && <Stack
                    sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        width: '100%',
                        mt: 2,
                    }}
                >
                    <Button
                        variant='outlined'
                        size='small'
                        disabled={isGeneratingPdf || reportRowCount === 0}
                        onClick={() => {
                            void downloadPdf(personId);
                        }}
                    >
                        {isGeneratingPdf ? 'Preparing PDF...' : 'Download as PDF'}
                    </Button>
                </Stack>}
                {pdfError !== '' && <Stack
                    sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Alert severity='error' sx={{mt: 1}}>{pdfError}</Alert>
                </Stack>}
            </Stack>
        }}
    />;
};

export default RentalsList;
